import { useState } from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';
import type { PricingCatalogFeature } from '../../lib/pricingCatalog';
import { interRegular } from '../../lib/fonts';
import { styles } from './pricingPageStyles';
import { resolveMessage } from '../../lib/i18n/messages';

type PricingFeatureItemProps = {
    feature: PricingCatalogFeature;
    fontsLoaded: boolean;
};

function QuestionMarkIcon() {
    return (
        <Svg width={15} height={15} viewBox="0 0 24 24" fill="none">
            <Circle cx={12} cy={12} r={9.25} stroke="#4f7f38" strokeWidth={2} />
            <Path
                d="M9.45 9.15C9.73 7.72 10.82 6.9 12.21 6.9C13.78 6.9 14.88 7.9 14.88 9.25C14.88 10.32 14.34 10.92 13.35 11.58C12.45 12.18 12.1 12.67 12.1 13.62V14.08"
                stroke="#4f7f38"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
            />
            <Path
                d="M12.09 17.08H12.11"
                stroke="#4f7f38"
                strokeLinecap="round"
                strokeWidth={2.75}
            />
        </Svg>
    );
}

export default function PricingFeatureItem({
    feature,
    fontsLoaded,
}: PricingFeatureItemProps) {
    const { i18n } = useLingui();
    const [isOpen, setIsOpen] = useState(false);
    const description = feature.description?.trim() || feature.name;

    return (
        <View style={styles.featureItem}>
            <Text style={styles.checkmark}>✓</Text>
            <View style={styles.featureContent}>
                <View style={styles.featureLine}>
                    <Text style={[styles.featureText, fontsLoaded && interRegular]}>
                        {feature.text}
                    </Text>
                    <TouchableOpacity
                        accessibilityLabel={resolveMessage(i18n, msg({
                            id: 'pricing.feature.aboutAccessibility',
                            message: 'What is {feature}?',
                        }), { feature: feature.text })}
                        accessibilityRole="button"
                        activeOpacity={0.72}
                        onPress={() => setIsOpen((current) => !current)}
                        style={styles.featureHelpButton}
                        {...(Platform.OS === 'web' && {
                            'aria-expanded': isOpen,
                        })}
                    >
                        <QuestionMarkIcon />
                    </TouchableOpacity>
                </View>
                {isOpen ? (
                    <View style={styles.featureTooltip}>
                        <Text style={[styles.featureTooltipText, fontsLoaded && interRegular]}>
                            {description}
                        </Text>
                    </View>
                ) : null}
            </View>
        </View>
    );
}
